/**
 * Search, filters, sort and paging done in the browser, for the list tables whose API returns the
 * whole list at once (staff accounts, the status master, request analytics' paths). They get the
 * same controls as the server-paged tables (Medicines, Doctors), without a request per keystroke.
 */

import { useCallback, useState } from "react";

export type SortValue = string | number | boolean | null | undefined;

/** Every word of `q` appears in `text`, case aside. A run of 3+ digits also matches the digits of
 *  a phone number written with spaces, dashes or a +91 in front ("98765 43210" finds +919876543210). */
export function matchesSearch(text: string, q: string): boolean {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = text.toLowerCase();
  if (words.every((w) => haystack.includes(w))) return true;
  const digits = q.replace(/\D/g, "");
  return digits.length >= 3 && text.replace(/\D/g, "").includes(digits);
}

/** Ascending order of two non-empty sort values: numbers by size, text by the alphabet with its
 *  numbers read as numbers ("Status 2" before "Status 10"). */
function compareValues(a: Exclude<SortValue, null | undefined>, b: Exclude<SortValue, null | undefined>): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export interface ListQuery<T> {
  /** The search box. */
  q: string;
  /** What a row is searched by. */
  searchText: (row: T) => string;
  /** The filters: a row stays when this says so. */
  keep?: (row: T) => boolean;
  /** The value a row sorts by. */
  sortValue: (row: T) => SortValue;
  descending: boolean;
}

/** The rows that match, in order. Rows without a value to sort by go last either way; ties keep
 *  the order the API gave. */
export function narrowAndSort<T>(rows: readonly T[], { q, searchText, keep, sortValue, descending }: ListQuery<T>): T[] {
  const term = q.trim();
  const kept = rows.filter((r) => (!keep || keep(r)) && (!term || matchesSearch(searchText(r), term)));
  const keyed = kept.map((row) => ({ row, value: sortValue(row) }));
  keyed.sort((x, y) => {
    const xEmpty = x.value === null || x.value === undefined || x.value === "";
    const yEmpty = y.value === null || y.value === undefined || y.value === "";
    if (xEmpty || yEmpty) return Number(xEmpty) - Number(yEmpty);
    const order = compareValues(x.value!, y.value!);
    return descending ? -order : order;
  });
  return keyed.map((k) => k.row);
}

/**
 * The first `perPage` rows, and a page more each time the table asks (scrolled near the end).
 * Back to the first page whenever `resetKey` changes: pass one that changes with the search,
 * filters, sort and page size.
 */
export function useClientPages<T>(rows: T[], perPage: number, resetKey: string) {
  const [paging, setPaging] = useState({ key: resetKey, shown: perPage });
  // Forget the old count as soon as the key changes, so going back to an earlier search starts
  // at its first page too, rather than where that search had got to.
  if (paging.key !== resetKey) setPaging({ key: resetKey, shown: perPage });
  const shown = paging.key === resetKey ? paging.shown : perPage;
  const loadMore = useCallback(
    () => setPaging((p) => ({ key: resetKey, shown: (p.key === resetKey ? p.shown : perPage) + perPage })),
    [resetKey, perPage],
  );
  return { page: rows.slice(0, shown), total: rows.length, hasMore: shown < rows.length, loadMore };
}
